import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Routes } from '../../config/routes';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Stripe } from '../../components/shared/http/request-interfaces';

export interface CreateConnectAccountResponse {
    clientSecret: string;
    publishableKey: string;
}

export interface AccountSessionResponse {
    clientSecret: string;
    publishableKey: string;
}

export interface ConnectAccountStatusResponse {
    status: 'verified' | 'pending' | 'incomplete' | 'restricted' | 'not_started';
    treasuryCapability: string | null;
    requirementsCurrentlyDue: string[];
    requirementsPendingVerification: string[];
    disabledReason: string | null;
}

@Injectable({
    providedIn: 'root',
})
export class StripeConnectService {
    constructor(private _http: ApplicationHttpClient) {}

    /**
     * Creates a new Stripe Connect account for the organization and returns
     * an account session client secret for the embedded onboarding component.
     */
    public createConnectAccount(
        request: Stripe.ConnectAccountSessions.CreateConnectAccountSessionRequest,
    ): Observable<CreateConnectAccountResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.StripeModule.CreateConnectAccount(request.organizationId),
        );
        return this._http.post<CreateConnectAccountResponse>(url, JSON.stringify(request));
    }

    /**
     * Creates an account session for the embedded onboarding component.
     * The client secret is used to initialize the Stripe Connect embedded component.
     */
    public createAccountSession(
        request: Stripe.ConnectAccountSessions.ReadConnectAccountSessionRequest,
    ): Observable<AccountSessionResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.StripeModule.CreateConnectAccountSession(
                request.organizationId,
                request.stripeConnectAccountId,
            ),
        );
        return this._http.post<AccountSessionResponse>(url, JSON.stringify(request));
    }

    public getConnectAccountStatus(organizationId: number): Observable<ConnectAccountStatusResponse> {
        const url = Routes.MakeLegFiCoreUrl(Routes.StripeModule.ConnectAccountStatus(organizationId));
        return this._http.get<ConnectAccountStatusResponse>(url);
    }

    /**
     * Creates an account session for a specific embedded component (e.g., issuing-cards-list).
     * Resolves the Connect account automatically — only needs org ID and usage.
     */
    public createComponentSession(
        organizationId: number,
        usage: string,
    ): Observable<AccountSessionResponse> {
        const url = Routes.MakeLegFiCoreUrl(
            Routes.StripeModule.ComponentSession(organizationId),
        );
        return this._http.post<AccountSessionResponse>(url, JSON.stringify({ usage }));
    }
}
